import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PixCheckout from './PixCheckout';
import { formatCurrency } from '../utils/image';
import tokens from '../theme/tokens';

/**
 * Aviso de mensalidade em aberto ao entrar no app: mostra o total pendente e, ao tocar em
 * "Gerar pagamento", o QR Code/PIX copia e cola da cobrança (uma por vez, da mais antiga).
 * `pendentes`: cobranças com status pending de /pagamentos/me.
 */
const MensalidadeAbertaDialog = ({ open, pendentes, onClose }) => {
  const [pagando, setPagando] = useState(null); // cobrança com o PIX na tela
  const [pagos, setPagos] = useState([]); // ids confirmados nesta abertura

  const abertos = pendentes.filter((p) => !pagos.includes(p._id));
  const total = abertos.reduce((t, p) => t + Number(p.amount || 0), 0);
  const maisAntiga = [...abertos].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];

  const fechar = () => {
    setPagando(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={fechar} fullWidth maxWidth="xs" scroll="body">
      {pagando ? (
        <>
          <DialogContent sx={{ pt: 2 }}>
            <Button size="small" color="inherit" startIcon={<ArrowBackRoundedIcon />} onClick={() => setPagando(null)} sx={{ mb: 1.5 }}>
              Voltar
            </Button>
            <PixCheckout
              key={pagando._id}
              token={pagando.token}
              autoGerar
              onStatus={(status) => status === 'paid' && setPagos((atual) => [...atual, pagando._id])}
              acaoPago={
                abertos.length > 0 ? (
                  <Button variant="contained" onClick={() => setPagando(null)}>Ver o que falta ({abertos.length})</Button>
                ) : (
                  <Button variant="contained" onClick={fechar}>Concluir</Button>
                )
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={fechar} color="inherit">Pagar depois</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent sx={{ pt: 4, textAlign: 'center' }}>
            <Box sx={{ width: 72, height: 72, mx: 'auto', borderRadius: '50%', bgcolor: tokens.color.warningSoft, color: tokens.color.warning, display: 'grid', placeItems: 'center', mb: 2 }}>
              <ReceiptLongRoundedIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6">{abertos.length === 1 ? 'Você tem uma mensalidade em aberto' : 'Você tem mensalidades em aberto'}</Typography>
            <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 700, fontSize: '2.4rem', color: tokens.color.warning, my: 1 }}>
              {formatCurrency(total)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quer gerar o pagamento agora? O PIX é confirmado na hora e o valor sai do seu saldo em aberto.
            </Typography>

            {abertos.length > 1 && (
              <Stack sx={{ mt: 2, textAlign: 'left', border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                {abertos.map((p) => (
                  <ButtonBase key={p._id} onClick={() => setPagando(p)} sx={{ px: 1.5, py: 1.25, justifyContent: 'flex-start', textAlign: 'left' }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{p.description}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatCurrency(p.amount)}</Typography>
                    </Box>
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 700, mr: 0.5 }}>Pagar</Typography>
                    <ChevronRightRoundedIcon fontSize="small" color="primary" />
                  </ButtonBase>
                ))}
              </Stack>
            )}
            {abertos.length > 1 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Cada mensalidade tem o seu PIX. Comece pela mais antiga ou toque na que quer pagar.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={fechar} color="inherit">Agora não</Button>
            {maisAntiga && (
              <Button variant="contained" startIcon={<PixRoundedIcon />} onClick={() => setPagando(maisAntiga)}>
                {abertos.length > 1 ? 'Pagar a mais antiga' : 'Gerar pagamento'}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default MensalidadeAbertaDialog;
